 //mini-reel splitting for single-PDF optimization.
 //each mini-reel is ~10% of the total labels, with:
 //- Minimum 50 labels per mini-reel
 //- Maximum 1000 mini-reels

import type { ReelAssignment } from '../workers/types';

export interface MiniReelConfig {
  reelCount: number;
  labelsPerReel: number;
  reels: ReelAssignment[];
}

const MIN_LABELS_PER_REEL = 50;
const MAX_MINI_REELS = 1000;

const TARGET_REEL_FRACTION = 0.1;

const MIN_LABELS_TO_SPLIT = MIN_LABELS_PER_REEL + 1;

export function computeMiniReels(totalLabels: number, jobSlug: string, labelsPerPage?: number): MiniReelConfig {
  const total = Math.max(1, Math.floor(totalLabels));

  //dont split very small jobs
  if (total < MIN_LABELS_TO_SPLIT) {
    return {
      reelCount: 1,
      labelsPerReel: total,
      reels: [{
        reelNum: 1,
        labelCount: total,
        qrIndexOffset: 0,
        rangeStart: 1,
        rangeEnd: total,
        idbKey: `pdf-${jobSlug}-mr-1`,
        filename: `${jobSlug}.pdf`,
      }],
    };
  }

  let labelsPerReel = Math.max(
    MIN_LABELS_PER_REEL,
    Math.ceil(total * TARGET_REEL_FRACTION),
  );

  if (labelsPerPage && labelsPerPage > 1) {
    labelsPerReel = Math.ceil(labelsPerReel / labelsPerPage) * labelsPerPage;
  }

  let reelCount = Math.ceil(total / labelsPerReel);

  if (reelCount > MAX_MINI_REELS) {
    reelCount = MAX_MINI_REELS;
    labelsPerReel = Math.ceil(total / reelCount);
    //re-align after capping
    if (labelsPerPage && labelsPerPage > 1) {
      labelsPerReel = Math.ceil(labelsPerReel / labelsPerPage) * labelsPerPage;
    }
    reelCount = Math.ceil(total / labelsPerReel);
  }

  const reels: ReelAssignment[] = [];
  for (let i = 0; i < reelCount; i++) {
    const startIndex = i * labelsPerReel;
    const reelLabelCount = Math.min(labelsPerReel, total - startIndex);
    const reelNum = i + 1;

    reels.push({
      reelNum,
      labelCount: reelLabelCount,
      qrIndexOffset: startIndex,
      rangeStart: startIndex + 1,
      rangeEnd: startIndex + reelLabelCount,
      //use "mr" (mini-reel) prefix to distinguish from user reels
      idbKey: `pdf-${jobSlug}-mr-${reelNum}`,
      filename: `${jobSlug}-mr-${reelNum}.pdf`,
    });
  }

  return { reelCount, labelsPerReel, reels };
}

export function shouldUseMiniReels(totalLabels: number): boolean {
  return totalLabels >= MIN_LABELS_TO_SPLIT;
}
